import styled from '@emotion/styled';
import { useEffect, useState } from 'react';

import Button from '@/components/Buttons/Button';
import { useGetSupportUrl } from '@/hooks/useGetSupportUrl';
import logo from '@/images/did_logo_dark.svg';
import { useAnalytics } from '@/services/analytics';
import { sendEmailVerification } from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { Typography } from '@mui/material';
import BottomDotsSvg from '../../images/BottomDots.svg';
import TopDotsSvg from '../../images/TopDots.svg';

const TopDots = styled.img`
    position: fixed;
    top: 0;
    right: 0;
`;

const BottomDots = styled.img`
    position: fixed;
    bottom: 0;
    left: 0;
`;

const StyledA = styled.a`
    font-weight: bold;
    text-decoration: none;
    color: ${({ theme }) => theme.colors.black.main};
`;

const Title = styled(Typography)`
    line-height: 8rem;
    font-size: 2.4rem;
`;

const MainDescription = styled(Typography)`
    margin: 2rem 0;
    text-align: center;
`;

const Page = styled.div`
    display: flex;
    position: relative;
    background: #fff;
    width: 100%;
    height: 100%;
`;

const LogoContainer = styled.img`
    width: 58px;
`;

const Card = styled.div`
    width: 400px;
    border-radius: 8px;
    box-shadow: 0px 12px 40px 0px #0000001f;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 4rem;
    padding-bottom: 3rem;
    margin-bottom: 1rem;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
`;

const Cta = styled(Button)`
    margin: 2rem 0;
`;

const RESEND_TIMER_IN_SECONDS = 30;

const EmailNotVerified = () => {
    const mixpanel = useAnalytics();
    const supportUrl = useGetSupportUrl();
    const [resendTimer, setResendTimer] = useState(RESEND_TIMER_IN_SECONDS);
    const [isSending, setIsSending] = useState(false);
    const { user, loginWithRedirect } = useAuth();

    useEffect(() => {
        mixpanel?.track('email-not-verified', { event: 'view' });
    }, []);

    const onClick = async () => {
        try {
            mixpanel?.track('email-not-verified', { event: 'click' });
            setIsSending(true);
            await sendEmailVerification();
        } catch (e) {
            logger.error('could not send email verification', { e });
        } finally {
            setIsSending(false);
            setResendTimer(RESEND_TIMER_IN_SECONDS);
        }
    };

    const onUseDifferentEmailClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        loginWithRedirect({ prompt: 'login' });
    };

    useEffect(() => {
        const interval = setInterval(() => {
            if (resendTimer > 0) {
                setResendTimer(time => time - 1);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    return (
        <Page>
            <Body>
                <TopDots src={TopDotsSvg} />
                <BottomDots src={BottomDotsSvg} />
                <Card>
                    <LogoContainer src={logo} />
                    <Title>Please verify your email</Title>
                    <Typography variant="body2" lineHeight="2rem">
                        We sent an email to
                    </Typography>
                    <Typography variant="h5">{user?.email ?? ''}</Typography>
                    <MainDescription variant="body2" lineHeight="2rem">
                        Click on the link in that email to complete your signup. <br /> If you don’t see it,
                        <strong> check your spam folder</strong>.
                    </MainDescription>
                    {user?.isPrimary === false && (
                        <Typography variant="h5" lineHeight="4rem">
                            Once verified you will need to sign in to the studio again.
                        </Typography>
                    )}
                    <Typography variant="body2">Can't find the email?</Typography>
                    <Cta color="secondary" onClick={onClick} disabled={isSending || resendTimer > 0}>
                        Resend&nbsp;<span style={{ display: resendTimer > 0 ? 'inline-block' : 'none' }}>in {resendTimer}</span>
                    </Cta>
                    <Typography variant="body2">
                        Still didn’t get it? <StyledA href={supportUrl}>Contact support</StyledA>
                    </Typography>
                    <Typography variant="body2" lineHeight="4rem">
                        or{' '}
                        <StyledA href="" onClick={onUseDifferentEmailClick}>
                            use a different email
                        </StyledA>
                    </Typography>
                </Card>
                <footer>
                    <Typography variant="body2" textAlign="center" lineHeight="2rem">
                        By signing up, I confirm that I have read and accepted
                        <br />
                        D-ID’s{' '}
                        <a href="https://www.d-id.com/studio-end-user-license-agreement/" target="_blank">
                            Terms & Conditions
                        </a>{' '}
                        and&nbsp;
                        <a href="https://www.d-id.com/studio-privacy/" target="_blank">
                            Privacy Policy.
                        </a>
                    </Typography>
                </footer>
            </Body>
        </Page>
    );
};

export default EmailNotVerified;
